<script lang="ts">
  import type { GatFormFieldProps } from '../gat-form-field'

  export type GatTextEditModelValue = string | null | undefined

  export interface GatTextEditProps
    extends GatFormFieldProps<GatTextEditModelValue> {
    /** The value of the input */
    modelValue?: GatTextEditModelValue
    /** The default value of the input */
    defaultValue?: GatTextEditModelValue
    /** If the input should be focused when the component is mounted */
    autofocus?: boolean
    /** Input placeholder if the input is empty */
    placeholder?: string
    /** Forces input-value to be upper-case */
    upperCase?: boolean
    /** Forces input-value to be lower-case */
    lowerCase?: boolean
    /** Forces the maximum allowed characters for the input */
    maxLength?: number
  }

  export type GatTextEditEmits = {
    /** Emitted when the input value is changed */
    (e: 'update:modelValue', value: GatTextEditModelValue): void
    /** Emitted when the input value is input */
    (e: 'input', value: GatTextEditModelValue): void
    /** Emitted when the input loses focus */
    (e: 'blur', event: FocusEvent): void
    /** Emitted when the input gains focus */
    (e: 'focus', event: FocusEvent): void
    /** Emitted when the input keydown event is triggered */
    (e: 'keydown', event: KeyboardEvent): void
    /** Emitted when the input is cleared */
    (e: 'click:clear'): void
  }
</script>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue'
  import { useTextEditModelValue } from './useTextEditModelValue'
  import { GatFormField } from '../gat-form-field'

  const props = withDefaults(defineProps<GatTextEditProps>(), {
    showOptional: undefined,
    validateOn: 'input',
    hideDetails: 'auto',
    defaultValue: undefined,
    autocomplete: 'off'
  })

  const emit = defineEmits<GatTextEditEmits>()

  const loading = defineModel<boolean>('loading', {
    default: false,
    required: false
  })

  const isFocused = ref(false)

  const formField = useTemplateRef('form-field')

  const { textEditValue } = useTextEditModelValue(
    props,
    emit,
    props.defaultValue
  )
  const input = useTemplateRef('input')

  onMounted(() => {
    if (props.autofocus) {
      focus()
    }
  })

  async function focus() {
    await nextTick()
    if (input.value) {
      input.value.focus?.()
    }
  }

  defineExpose({
    validate: formField.value?.validate(),
    focus,
    loading,
    isFocused: computed(() => isFocused.value)
  })

  function handleValueChange(value: GatTextEditModelValue) {
    nextTick(() => {
      emit('update:modelValue', value)
    })
  }
</script>

<template>
  <gat-form-field
    ref="form-field"
    v-bind="props"
    :value="textEditValue"
    @clear="[emit('click:clear'), emit('update:modelValue', undefined)]"
    @value-updated="handleValueChange"
  >
    <template #default="{ errorMessages, isValid, name, disabled, inputClass }">
      <input
        ref="input"
        :id="name"
        v-model="textEditValue"
        :data-testid="dataTestid"
        type="text"
        :placeholder
        :autocomplete="autocomplete"
        :disabled="disabled"
        :maxLength
        :class="inputClass"
        @input="($event) => [emit('input', ($event as any).target?.value)]"
        @blur="[
          emit('blur', $event),
          formField?.handleBlur($event),
          formField?.setHideDetails(
            props.hideDetails === 'auto' && errorMessages.length === 0
              ? false
              : undefined
          ),
          (isFocused = false)
        ]"
        @focus="[
          formField?.setHideDetails(
            props.hideDetails === 'auto' ? true : undefined
          ),
          (isFocused = true),
          emit('focus', $event)
        ]"
        @keydown="[emit('keydown', $event)]"
      />
    </template>
    <template #append-inner>
      <slot name="append-inner" />
    </template>
    <template #prepend-inner v-if="prependInnerIcon || $slots['prepend-inner']">
      <slot name="prepend-inner" />
    </template>
    <template #messages>
      <slot name="messages" />
    </template>
    <template #message-short="props">
      <slot name="message-short" v-bind="props" />
    </template>
    <template #messages-long="props">
      <slot name="messages-long" v-bind="props" />
    </template>
  </gat-form-field>
</template>
